import _ from "lodash";

export default {
    computed: {
        isDarkMode() {
            return (this.$store?.state?.theme === 'dark') ||
                   (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark');
        },
        monthChartOptions() {
            const formatHours = this.formatLoggedHours;
            const textColor = this.isDarkMode ? "#e2e8f0" : "#495057";
            return {
                responsive: true,
                maintainAspectRatio: false,
                cutout: "68%",
                layout: {
                    padding: {
                        top: 4,
                        bottom: 4
                    }
                },
                plugins: {
                    legend: {
                        position: "bottom",
                        labels: {
                            boxWidth: 10,
                            boxHeight: 10,
                            usePointStyle: true,
                            pointStyle: "circle",
                            padding: 10,
                            color: textColor,
                            font: {
                                size: 11,
                                family: "Segoe UI, sans-serif"
                            }
                        }
                    },
                    tooltip: {
                        enabled: false,
                        external: this.customTooltipHandler,
                        animation: false,
                        padding: 8,
                        boxPadding: 4,
                        titleFont: {
                            size: 12,
                            weight: "bold",
                            family: "Segoe UI, sans-serif"
                        },
                        bodyFont: {
                            size: 11,
                            family: "Segoe UI, sans-serif"
                        },
                        callbacks: {
                            label(context) {
                                const value = Number(context.raw) || 0;
                                const total = _.sum(context.dataset.data) || 0;
                                const percent = total > 0 ? Math.round((value / total) * 100) : 0;
                                return ` ${formatHours(value)} hrs \u00B7 ${percent}%`;
                            }
                        }
                    }
                }
            };
        }
    },
    methods: {
        customTooltipHandler(context) {
            const { chart, tooltip } = context || {};
            const parent = chart && chart.canvas && chart.canvas.parentNode;
            if (!parent) return;

            let tooltipEl = parent.querySelector(".chartjs-custom-tooltip");
            if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.className = "chartjs-custom-tooltip";
                tooltipEl.style.opacity = "0";
                tooltipEl.style.pointerEvents = "none";
                parent.appendChild(tooltipEl);
            }

            const centerEl = parent.querySelector(".month-worktype-chart-center");

            if (!tooltip || tooltip.opacity === 0) {
                tooltipEl.style.opacity = "0";
                tooltipEl.style.pointerEvents = "none";
                if (centerEl) {
                    centerEl.classList.remove("is-hidden");
                }
                return;
            }

            if (centerEl) {
                centerEl.classList.add("is-hidden");
            }

            if (tooltip.body) {
                const titleLines = tooltip.title || [];
                const bodyLines = tooltip.body.map(b => b.lines);

                let innerHtml = "";
                if (titleLines.length) {
                    innerHtml += `<div class="chartjs-custom-tooltip-title">${titleLines.join(" ")}</div>`;
                }
                innerHtml += '<div class="chartjs-custom-tooltip-body">';
                bodyLines.forEach((lines, i) => {
                    const colors = (tooltip.labelColors && tooltip.labelColors[i]) || {};
                    const bg = colors.backgroundColor || "#868e96";
                    const border = colors.borderColor || "transparent";
                    const text = Array.isArray(lines) ? lines.join(" ") : lines;
                    innerHtml += `<div class="chartjs-custom-tooltip-item">
                        <span class="chartjs-custom-tooltip-swatch" style="background-color: ${bg}; border-color: ${border};"></span>
                        <span class="chartjs-custom-tooltip-text">${text}</span>
                    </div>`;
                });
                innerHtml += "</div>";
                tooltipEl.innerHTML = innerHtml;
            }

            const canvas = chart.canvas;
            const left = canvas.offsetLeft + (typeof tooltip.x === "number" ? tooltip.x : 0);
            const top = canvas.offsetTop + (typeof tooltip.y === "number" ? tooltip.y : 0);

            if (typeof tooltip.caretX === "number" && typeof tooltip.x === "number") {
                const tooltipWidth = tooltip.width || 60;
                const caretOffset = Math.max(10, Math.min(Math.max(tooltipWidth - 10, 10), tooltip.caretX - tooltip.x));
                tooltipEl.style.setProperty("--caret-x", `${caretOffset}px`);
            }

            tooltipEl.setAttribute("data-y-align", tooltip.yAlign || "center");
            tooltipEl.setAttribute("data-x-align", tooltip.xAlign || "center");
            tooltipEl.style.left = `${left}px`;
            tooltipEl.style.top = `${top}px`;
            tooltipEl.style.opacity = "1";
            tooltipEl.style.pointerEvents = "none";
        },
        chooseProgressStyle(val, total) {
            if (val < total)
                return "secondary";
            if (val === total)
                return "success";
            if (val > total)
                return "danger";
        },
        chooseProgressStyleByWorkType(logType, logTypeName) {
            switch ((logType || "").toLowerCase()) {
                case "work":
                    switch ((logTypeName || "").toLowerCase()) {
                        case "development":
                            return "#2f9e44";
                        case "testing":
                            return "#1971c2";
                        case "code review":
                            return "#0ca678";
                        case "technical analysys":
                        case "technical analysis":
                            return "#12b886";
                        case "business analysys":
                        case "business analysis":
                            return "#fab005";
                        case "meeting":
                            return "#15aabf";
                        case "blocked":
                            return "#fd7e14";
                    }
                    break;
                case "activity":
                    switch ((logTypeName || "").toLowerCase()) {
                        case "planning":
                            return "#4c6ef5";
                        case "course":
                            return "#3b5bdb";
                        case "training":
                            return "#4dabf7";
                        case "interview":
                            return "#20c997";
                        case "meeting":
                            return "#22b8cf";
                        case "idle":
                            return "#fa5252";
                        case "other activity":
                            return "#845ef7";
                    }
                    break;
                case "absence":
                    switch ((logTypeName || "").toLowerCase()) {
                        case "vacation":
                        case "annual leave":
                            return "#82c91e";
                        case "holiday":
                            return "#ffd43b";
                        case "sick leave":
                            return "#ff922b";
                        case "child sick leave":
                            return "#ff6b6b";
                        case "unpaid leave":
                            return "#868e96";
                        case "personal leave":
                            return "#74c0fc";
                        case "parental leave":
                            return "#b197fc";
                        case "maternity leave":
                            return "#f783ac";
                        case "paternity leave":
                            return "#66d9e8";
                        case "marriage leave":
                            return "#ffa8a8";
                        case "bereavement leave":
                            return "#495057";
                        case "blood donation":
                            return "#e03131";
                        case "compensatory time off":
                            return "#63e6be";
                        case "work from home":
                            return "#339af0";
                        case "business trip":
                            return "#7048e8";
                        case "jury duty":
                            return "#fcc419";
                        case "military leave":
                            return "#5c7cfa";
                        case "study leave":
                            return "#22b8cf";
                        case "other leave":
                            return "#9775fa";
                    }
                    break;
            }
            return "#868e96";
        },
        getWorkTypePieData(hours) {
            const items = hours || [];
            const labels = _.map(items, (t) => t.logTypeName);
            const bgColors = _.map(items, (t) => this.chooseProgressStyleByWorkType(t.logType, t.logTypeName));
            const values = _.map(items, (t) => t.hours);
            return {
                labels,
                datasets: [
                    {
                        backgroundColor: bgColors,
                        borderColor: this.isDarkMode ? "#1a2332" : "#ffffff",
                        borderWidth: 3,
                        hoverOffset: 6,
                        data: values
                    }
                ]
            };
        },
        formatLoggedHours(value) {
            const hours = Number(value) || 0;
            return Number.isInteger(hours) ? hours : hours.toFixed(1);
        },
        hourLabel(value) {
            return Number(value) === 1 ? "hour" : "hours";
        },
        progressCaption(val, total) {
            const logged = Number(val) || 0;
            const capacity = Number(total) || 0;
            const remaining = capacity - logged;
            const percent = capacity > 0 ? Math.round((logged / capacity) * 100) : 0;
            if (remaining > 0) {
                const remainingPercent = capacity > 0 ? Math.round((remaining / capacity) * 100) : 0;
                return `${this.formatLoggedHours(remaining)} hrs remaining \u00B7 ${remainingPercent}%`;
            }
            if (remaining === 0)
                return `Complete \u00B7 ${percent}%`;
            return `${this.formatLoggedHours(Math.abs(remaining))} hrs overtime \u00B7 ${percent}%`;
        },
        targetCaption(loggedToDate, expectedToDate) {
            const logged = Number(loggedToDate) || 0;
            const expected = Number(expectedToDate) || 0;
            const delta = expected - logged;
            if (delta > 0)
                return `${this.formatLoggedHours(delta)} ${this.hourLabel(delta)} behind`;
            if (delta === 0)
                return "On track";
            return `${this.formatLoggedHours(Math.abs(delta))} ${this.hourLabel(Math.abs(delta))} ahead`;
        }
    }
};